import { useState } from 'react';
import { PrimaryActionButton, StonePanel } from '@aztec-realms/ui';
import type { CivicExpeditionChoice } from './data/tribe-expedition-api';
import { CivicOutcomeArt } from './civic-expedition-art';
import { civicFamilyPresentation, civicProgressionBand } from './civic-expedition-presentation';

export function CivicExpeditionChoices({
  choices,
  canStart,
  disabled,
  safetyLocked,
  onStart,
}: {
  choices: CivicExpeditionChoice[];
  canStart: boolean;
  disabled: boolean;
  safetyLocked: boolean;
  onStart: (templateCode: string, branchCode: string) => void;
}) {
  const [selection, setSelection] = useState<{ template: string; branch: string }>();
  const interactionLocked = disabled || safetyLocked;
  const bounded = choices.slice(0, 3);
  const choice = bounded.find((item) => item.template_code === selection?.template) ?? bounded[0];
  const branch =
    choice?.branches.find((item) => item.code === selection?.branch) ?? choice?.branches[0];
  const band = civicProgressionBand(choice?.progression_band);
  if (!choice || !branch)
    return (
      <p>
        No new shared expedition is eligible right now. Your recorded achievements remain available.
      </p>
    );
  return (
    <section className="civic-expedition-choices" aria-labelledby="expedition-choice-title">
      <h3 id="expedition-choice-title">Choose your next shared goal</h3>
      <div
        className="civic-expedition-choices__templates"
        role="group"
        aria-label="Expedition families"
      >
        {bounded.map((item) => (
          <button
            type="button"
            key={item.template_code}
            aria-pressed={item.template_code === choice.template_code}
            aria-describedby={
              item.template_code === choice.template_code ? 'expedition-choice-summary' : undefined
            }
            disabled={interactionLocked}
            onClick={() =>
              setSelection({ template: item.template_code, branch: item.branches[0]?.code ?? '' })
            }
          >
            <small className="civic-expedition-choices__band">
              {civicFamilyPresentation(item.family_code).label} ·{' '}
              {civicProgressionBand(item.progression_band).label}
            </small>
            <strong>{item.title}</strong>
            {item.repeat_count > 0 && (
              <small>
                Played {item.repeat_count} time{item.repeat_count === 1 ? '' : 's'} in the last
                seven days
              </small>
            )}
          </button>
        ))}
      </div>
      <p id="expedition-choice-summary" className="civic-expedition-choices__summary">
        {choice.summary}
      </p>
      <fieldset disabled={interactionLocked} className="civic-expedition-choices__branches">
        <legend>How will you restore it?</legend>
        {choice.branches.map((item) => (
          <label key={item.code}>
            <input
              type="radio"
              name="expedition-branch"
              value={item.code}
              checked={branch.code === item.code}
              onChange={() => setSelection({ template: choice.template_code, branch: item.code })}
            />
            <span>
              <strong>{item.title}</strong>
              <span>{item.summary}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <StonePanel className="civic-expedition-choices__preview" data-branch-preview={branch.code}>
        <p className="civic-expedition-choices__depth">
          <strong>{band.label}</strong> · {band.description}
        </p>
        <div className="civic-expedition-choices__outcome">
          <div className="civic-expedition-choices__art" aria-hidden="true">
            <CivicOutcomeArt variant={branch.outcome.scene_variant} />
          </div>
          <div>
            <small>When your Tribe completes this goal</small>
            <h4>{branch.outcome.title}</h4>
            <p>{branch.outcome.description}</p>
          </div>
        </div>
        <ol>
          {branch.workstreams.map((role) => (
            <li key={role.code}>
              <strong>{role.title}</strong> · {role.objective}
            </li>
          ))}
        </ol>
        <p className="civic-expedition__hint">
          Seven days, one shared goal. Join before playing; each achievement counts once. There is
          no extra currency or XP for completing the expedition.
        </p>
        {canStart ? (
          <PrimaryActionButton
            disabled={interactionLocked}
            onClick={() => onStart(choice.template_code, branch.code)}
          >
            Start {branch.title}
          </PrimaryActionButton>
        ) : (
          <>
            <p>
              A leader or officer can start this shared goal. You can keep playing your own path.
            </p>
            {!safetyLocked && <p>Look under “Where next?” for your available personal path.</p>}
          </>
        )}
      </StonePanel>
    </section>
  );
}
